/**
 * FilmPreprintPage — PRE-PRINTED DTF film stock (Phase 50.02 pulled forward / BLU-233).
 *
 * Order-blind DTF film stock: print N films of a catalog design+size AHEAD of
 * demand, rack them onto a SHELF, and watch shelf stock. Coordinators (Admin,
 * Warehouse Coordinator) create batches; DTF Printers print and rack only. A
 * matching order later auto-claims a shelf film inside filmPrint.prepareFilm (the
 * Film-Print station shows a "pulled from shelf" cue instead of dropping to Fiery).
 *
 * Three sections:
 *   (a) Create batch — debounced design search → size + quantity → inline
 *       eligibility → add line → create the film_pre_print batch.
 *   (b) Worklist — the open batch's films, or with no batch open every stock
 *       film at this floor still waiting to print. Each film: "Print film" (reuses the Film-Print
 *       station's useFieryAgent Fiery-drop pattern) + "Rack to shelf" (a shelf-slot
 *       picker over filmStorage.getFilmStorage → markStockFilmStored).
 *   (c) Shelf stock — read-only design+size availability (getShelfStock).
 *
 * Production design language (PRODUCTION_DESIGN.md): semantic colour for state,
 * ≥48px targets, modal-level errors with audio, large glanceable type. This is a
 * coordinator management surface (no scanner), so no data-scanner-input wiring.
 *
 * BLU-717 S3: stock-film creation is a per-location permission
 * (`production_settings.film_preprint_enabled`). The gate is on the TRIGGER — a
 * floor that may not print stock films never gets the create form, it gets a
 * notice naming the floor and where to turn it on. Building a batch and only
 * learning at submit is the failure this replaces.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useLocation } from '@/contexts/LocationContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { ScanErrorModal } from '@/components/ui/scan-error-modal';
import { ConnectionStatusBanner } from '@/components/ui/connection-status-banner';
import { useScanFeedback } from '@/hooks/useScanFeedback';
import { useBackendHeartbeat } from '@/hooks/useBackendHeartbeat';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { usePreprintPermission } from '@/hooks/usePreprintPermission';
import { useRole } from '@/hooks/use-role';
import { useFieryAgent } from '@/features/film-print';
import { filmDropArgs } from '@/features/film-print/utils/film-file';
import { useFilmDownload } from '@/features/film-print/hooks/useFilmDownload';
import { trpc, type RouterOutputs } from '@/lib/trpc';
import { getUserMessage } from '@/lib/trpc-error-utils';
import { playSuccessBeep } from '@/lib/sounds';
import { cn } from '@/lib/utils';
import {
  Boxes, Search, Loader2, CheckCircle, AlertCircle, Send, Archive, ArrowLeft, X, Plus, WifiOff, Film,
  Settings, Download,
} from 'lucide-react';
import { ZoomImg } from '@/components/production/HoverZoom';

type DesignResult = RouterOutputs['filmPreprint']['searchDesigns']['designs'][number];
type StockFilm = RouterOutputs['filmPreprint']['getStockFilmsForBatch']['films'][number];

interface PreprintLine {
  shopifySku: string;
  title: string | null;
  size: string;
  quantity: number;
}

/** A film's size = the last `design_key` segment (owner:filename:size). */
function sizeFromDesignKey(designKey: string | null): string {
  if (!designKey) return '';
  return designKey.split(':').pop() ?? '';
}

export function FilmPreprintPage() {
  const { t } = useTranslation('production');
  const { selectedLocation } = useLocation();
  const heartbeat = useBackendHeartbeat();
  const feedback = useScanFeedback();
  const agent = useFieryAgent();
  const utils = trpc.useUtils();

  const locationId = selectedLocation?.id ?? '';

  // Coordinators create pre-print batches; a DTF Printer only prints and racks.
  const { isAdmin, isWarehouseCoordinator } = useRole();
  const canCreate = isAdmin() || isWarehouseCoordinator();

  // BLU-717 S3: may this floor create stock films at all? `blocked` is only ever
  // true on a positive `film_preprint_enabled: false` read — an unknown answer
  // (cross-org floor, still loading, non-coordinator) leaves creation open and
  // lets the backend gate rule. `isLoading` holds the form back for the first
  // read so a slow link can't show a form that is about to be withdrawn.
  const filmPreprintPermission = usePreprintPermission(locationId, 'film');
  const gateLocationName = filmPreprintPermission.locationName ?? selectedLocation?.name ?? '';

  // --- Create-batch state ---
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebouncedValue(query.trim(), 300);
  const [selectedDesign, setSelectedDesign] = useState<DesignResult | null>(null);
  const [size, setSize] = useState('');
  const debouncedSize = useDebouncedValue(size.trim(), 400);
  const [quantity, setQuantity] = useState(1);
  const [lines, setLines] = useState<PreprintLine[]>([]);

  // --- Worklist state ---
  const [batchId, setBatchId] = useState<string | null>(null);
  // The create card shows only to a coordinator with no batch open.
  const showCreate = canCreate && !batchId;
  // Per-film in-flight action ('printing' = drop to Fiery, 'racking' = store).
  const [filmBusy, setFilmBusy] = useState<Record<string, 'printing' | 'racking'>>({});
  // The stock film awaiting a shelf-slot choice (opens the picker dialog).
  const [rackTarget, setRackTarget] = useState<StockFilm | null>(null);
  // Stock films whose drop did not happen (agent offline, no payload, drop
  // failed): they offer "Download film" until a drop or a rack succeeds.
  const [needsManualFilmIds, setNeedsManualFilmIds] = useState<Set<string>>(new Set());
  // Download film: the hook owns the single-flight guard and the button state.
  const filmDownload = useFilmDownload({ onWarn: feedback.triggerWarning });

  // --- Queries ---
  const searchDesignsQuery = trpc.filmPreprint.searchDesigns.useQuery(
    { locationId, query: debouncedQuery },
    { enabled: canCreate && !!locationId && debouncedQuery.length >= 1, staleTime: 30_000 },
  );
  const eligibilityQuery = trpc.filmPreprint.checkEligibility.useQuery(
    { locationId, shopifySku: selectedDesign?.sku ?? '', size: debouncedSize },
    { enabled: canCreate && !!locationId && !!selectedDesign && debouncedSize.length >= 1, staleTime: 10_000 },
  );
  // With a batch open: that batch's films. With none: every stock film at this
  // floor still waiting to print (the DTF Printer's whole worklist).
  const filmsQuery = trpc.filmPreprint.getStockFilmsForBatch.useQuery(
    batchId ? { locationId, batchId } : { locationId },
    { enabled: !!locationId, staleTime: 5_000 },
  );
  const shelfStockQuery = trpc.filmPreprint.getShelfStock.useQuery(
    { locationId },
    { enabled: !!locationId, staleTime: 15_000 },
  );
  // Shelf slots for the rack picker — only fetched while the picker is open.
  const storageQuery = trpc.filmStorage.getFilmStorage.useQuery(
    { locationId },
    { enabled: !!locationId && rackTarget !== null, staleTime: 15_000 },
  );

  // --- Mutations ---
  const createBatchMutation = trpc.filmPreprint.createFilmPrePrintBatch.useMutation();
  // gcTime 0: a prepared film payload must not sit in the mutation cache after
  // the call; Download film runs its own prepare.
  const prepareMutation = trpc.filmPreprint.prepareStockFilm.useMutation({ gcTime: 0 });
  const storeMutation = trpc.filmPreprint.markStockFilmStored.useMutation();

  // Eligibility is "fresh" only when the debounced size matches the field — while
  // a newer size is still settling, the last result is stale, so Add is gated off.
  const eligibilityFresh = debouncedSize === size.trim();
  const canAddLine =
    !!selectedDesign
    && size.trim().length >= 1
    && quantity >= 1
    && eligibilityFresh
    && eligibilityQuery.data?.eligible === true;

  const totalFilms = lines.reduce((sum, l) => sum + l.quantity, 0);
  const shelfSlots = (storageQuery.data?.storage ?? []).filter((s) => s.storageType !== 'pigeon_hole');

  const handleAddLine = () => {
    if (!selectedDesign || !canAddLine) return;
    setLines((prev) => [
      ...prev,
      { shopifySku: selectedDesign.sku, title: selectedDesign.title, size: size.trim(), quantity },
    ]);
    // Reset the picker for the next line.
    setSelectedDesign(null);
    setSize('');
    setQuantity(1);
    setQuery('');
  };

  const handleRemoveLine = (idx: number) => setLines((prev) => prev.filter((_, i) => i !== idx));

  const handleCreateBatch = async () => {
    if (!locationId || lines.length === 0) return;
    try {
      const res = await createBatchMutation.mutateAsync({
        locationId,
        items: lines.map((l) => ({ shopifySku: l.shopifySku, size: l.size, quantity: l.quantity })),
      });
      setBatchId(res.batchId);
      setLines([]);
      playSuccessBeep();
      void utils.filmPreprint.getStockFilmsForBatch.invalidate();
      void utils.filmPreprint.getShelfStock.invalidate();
    } catch (error) {
      feedback.triggerError(t('filmPreprint.createFailedTitle'), getUserMessage(error));
    }
  };

  // "Print film" — compose the Fiery drop for one stock film + drop it via the
  // local print agent, REUSING the Film-Print station's useFieryAgent hook and
  // drop shape (composited bytes → raw presigned URL, filename prefixed with the
  // film id to avoid hot-folder basename collisions). Falls back to a manual-done
  // affordance (a warning naming the file to print by hand) when the agent is
  // offline or no drop payload could be produced.
  const markFilmNeedsManual = (filmId: string, needs: boolean) => {
    setNeedsManualFilmIds((s) => {
      if (s.has(filmId) === needs) return s;
      const next = new Set(s);
      if (needs) next.add(filmId);
      else next.delete(filmId);
      return next;
    });
  };

  const handlePrintFilm = async (film: StockFilm) => {
    if (!locationId) return;
    setFilmBusy((s) => ({ ...s, [film.id]: 'printing' }));
    try {
      const prep = await prepareMutation.mutateAsync({ locationId, filmUnitId: film.id });
      if (agent.hasDtf) {
        // The same builder Film Printing uses (same <id>__<name> filenames).
        const dropArgs = filmDropArgs(film.id, prep);
        if (dropArgs) {
          const drop = await agent.drop(dropArgs);
          if (!drop.success) {
            markFilmNeedsManual(film.id, true);
            feedback.triggerWarning(
              t('filmPreprint.dropFailedTitle'),
              t('filmPreprint.dropFailedBody', { error: drop.error ?? 'unknown' }),
            );
          } else {
            markFilmNeedsManual(film.id, false);
            playSuccessBeep();
          }
        } else {
          // No composited bytes and no presigned URL (e.g. Azure legacy backend) —
          // nothing to auto-drop; direct the operator to print it manually.
          markFilmNeedsManual(film.id, true);
          feedback.triggerWarning(
            t('filmPreprint.dropUnavailableTitle'),
            t('filmPreprint.dropUnavailableBody', { filename: prep.filename }),
          );
        }
      } else {
        // Agent offline / Fiery not configured — present the prepared file so the
        // operator can print it manually, then rack it.
        markFilmNeedsManual(film.id, true);
        feedback.triggerWarning(
          t('filmPreprint.agentOfflineTitle'),
          t('filmPreprint.agentOfflineBody', { filename: prep.filename }),
        );
      }
    } catch (error) {
      feedback.triggerError(t('filmPreprint.prepFailedTitle'), getUserMessage(error));
    } finally {
      setFilmBusy((s) => {
        const next = { ...s };
        delete next[film.id];
        return next;
      });
    }
  };

  // "Rack to shelf" — file the printed+cut film onto the chosen shelf slot.
  const handleRackToShelf = async (film: StockFilm, storageId: string) => {
    if (!locationId) return;
    setFilmBusy((s) => ({ ...s, [film.id]: 'racking' }));
    try {
      await storeMutation.mutateAsync({ locationId, filmUnitId: film.id, storageId });
      markFilmNeedsManual(film.id, false);
      playSuccessBeep();
      setRackTarget(null);
      void utils.filmPreprint.getStockFilmsForBatch.invalidate();
      void utils.filmPreprint.getShelfStock.invalidate();
    } catch (error) {
      feedback.triggerError(t('filmPreprint.rackFailedTitle'), getUserMessage(error));
    } finally {
      setFilmBusy((s) => {
        const next = { ...s };
        delete next[film.id];
        return next;
      });
    }
  };

  // "Download film" for a stock film whose drop did not happen. One prepare per
  // click (prepareStockFilm only reads and composites); bytes saved as
  // <id>__<name>, a storage URL opened in a new tab.
  const handleDownloadStockFilm = async (film: StockFilm) => {
    if (!locationId) return;
    await filmDownload.download(film.id, () => prepareMutation.mutateAsync({ locationId, filmUnitId: film.id }));
  };

  // One header for all three create-section states (permission unknown /
  // blocked / open) so the card geometry never jumps as the permission lands.
  const createCardHeader = (
    <CardHeader className="pb-3">
      <CardTitle className="text-lg flex items-center gap-2">
        <Plus className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
        {t('filmPreprint.createTitle')}
      </CardTitle>
    </CardHeader>
  );

  // Location guard.
  if (!selectedLocation) {
    return (
      <div className="container mx-auto px-6 py-6 max-w-7xl animate-enter">
        <Card>
          <CardContent className="py-16 text-center">
            <Boxes className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-lg text-muted-foreground">{t('filmPreprint.selectLocation')}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-6 py-6 max-w-7xl space-y-6 animate-enter flex flex-col min-h-[calc(100dvh-4.5rem)]">
      {/* ARIA live region (i18n, not hardcoded English). */}
      <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
        {searchDesignsQuery.isLoading && t('lookingUp')}
        {createBatchMutation.isPending && t('filmPreprint.creating')}
        {/* The permission lands after mount, so the withdrawal of the create form
            has to be announced — the page's own polite region, not a second one
            (PRODUCTION_DESIGN.md §13). */}
        {showCreate && filmPreprintPermission.blocked
          && t('filmPreprint.notEnabledTitle', { location: gateLocationName })}
      </div>

      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-indigo-500/10">
          <Boxes className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold">{t('filmPreprint.title')}</h1>
          <p className="text-muted-foreground">{t('filmPreprint.description')}</p>
        </div>
      </div>

      <ConnectionStatusBanner heartbeat={heartbeat} />

      {/* Agent / Fiery status banner (reuses the Film-Print copy). */}
      {!agent.isLoading && (!agent.isConnected || !agent.hasDtf) && (
        <div className="flex items-center gap-3 p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg">
          <WifiOff className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-amber-800 dark:text-amber-200">
              {!agent.isConnected ? t('filmPrint.agentNotDetected') : t('filmPrint.fieryNotConfigured')}
            </p>
            <p className="text-xs text-amber-700 dark:text-amber-300">
              {!agent.isConnected ? t('filmPrint.agentNotDetectedDesc') : t('filmPrint.fieryNotConfiguredDesc')}
            </p>
          </div>
        </div>
      )}

      <div className="flex-1 space-y-6">
        {/* (a0) PERMISSION UNKNOWN — hold the form back rather than offer one that
            may be withdrawn a moment later (Ningbo runs over a proxied link). */}
        {showCreate && filmPreprintPermission.isLoading && (
          <Card>
            {createCardHeader}
            <CardContent className="py-8 text-center">
              <Loader2 className="h-6 w-6 mx-auto animate-spin text-muted-foreground" aria-hidden />
            </CardContent>
          </Card>
        )}

        {/* (a1) NOT PERMITTED HERE — the gate, in place of the create form. Names
            the floor (a coordinator may run several) and where to turn it on;
            visible copy, no hover affordance (operators do not hover). */}
        {showCreate && filmPreprintPermission.blocked && (
          <Card>
            {createCardHeader}
            <CardContent>
              <div className="flex flex-col gap-3 rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30 p-4 sm:flex-row sm:items-start">
                <AlertCircle className="h-6 w-6 text-amber-600 dark:text-amber-400 shrink-0" aria-hidden />
                <div className="min-w-0 flex-1 space-y-1">
                  <p className="text-lg font-semibold text-amber-800 dark:text-amber-200">
                    {t('filmPreprint.notEnabledTitle', { location: gateLocationName })}
                  </p>
                  <p className="text-base text-amber-700 dark:text-amber-300">
                    {t('filmPreprint.notEnabledBody')}
                  </p>
                </div>
                {/* Same coordinator role set owns both this page and Production
                    Settings, so the fix is one tap away. h-12 per §4.3. */}
                <Button
                  asChild
                  variant="outline"
                  className="h-12 shrink-0 gap-2 border-amber-300 text-amber-800 hover:bg-amber-100 hover:text-amber-900 dark:border-amber-700 dark:text-amber-200 dark:hover:bg-amber-900/40"
                >
                  <Link to="/production/settings#preprint-permission">
                    <Settings className="h-5 w-5" aria-hidden />
                    {t('filmPreprint.notEnabledCta')}
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* (a) CREATE BATCH — hidden once a batch is created (worklist takes over). */}
        {showCreate && !filmPreprintPermission.isLoading && !filmPreprintPermission.blocked && (
          <Card>
            {createCardHeader}
            <CardContent className="space-y-4">
              {!selectedDesign ? (
                /* Design search */
                <div className="space-y-2">
                  <Label htmlFor="preprint-search">{t('filmPreprint.searchLabel')}</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" aria-hidden />
                    <Input
                      id="preprint-search"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder={t('filmPreprint.searchPlaceholder')}
                      className="h-12 pl-9 text-base"
                      autoComplete="off"
                    />
                  </div>
                  {debouncedQuery.length >= 1 && (
                    <div className="rounded-lg border divide-y">
                      {searchDesignsQuery.isLoading ? (
                        <div className="py-6 text-center"><Loader2 className="h-5 w-5 mx-auto animate-spin text-muted-foreground" /></div>
                      ) : (searchDesignsQuery.data?.designs ?? []).length === 0 ? (
                        <p className="px-4 py-6 text-base text-muted-foreground text-center">{t('filmPreprint.searchNoResults')}</p>
                      ) : (
                        (searchDesignsQuery.data?.designs ?? []).map((d) => (
                          <button
                            key={d.sku}
                            className="w-full px-4 py-3 flex items-center gap-3 hover:bg-muted/50 transition-colors text-left min-h-12"
                            onClick={() => setSelectedDesign(d)}
                          >
                            {d.image_url ? (
                              <ZoomImg src={d.image_url} alt="" decoding="async" className="h-10 w-10 rounded object-cover shrink-0" />
                            ) : (
                              <div className="h-10 w-10 rounded bg-muted flex items-center justify-center shrink-0"><Film className="h-5 w-5 text-muted-foreground" /></div>
                            )}
                            <div className="min-w-0 flex-1">
                              <p className="text-base font-medium truncate">{d.title ?? d.sku}</p>
                              <p className="font-mono text-sm text-muted-foreground truncate">{d.sku}</p>
                            </div>
                            {d.print_technology && <Badge variant="secondary" className="text-sm shrink-0">{d.print_technology}</Badge>}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              ) : (
                /* Selected design + size + quantity + eligibility */
                <div className="space-y-4">
                  <div className="flex items-center gap-3 rounded-lg border bg-muted/40 p-3">
                    {selectedDesign.image_url ? (
                      <ZoomImg src={selectedDesign.image_url} alt="" decoding="async" className="h-12 w-12 rounded object-cover shrink-0" />
                    ) : (
                      <div className="h-12 w-12 rounded bg-muted flex items-center justify-center shrink-0"><Film className="h-6 w-6 text-muted-foreground" /></div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-base font-semibold truncate">{selectedDesign.title ?? selectedDesign.sku}</p>
                      <p className="font-mono text-sm text-muted-foreground truncate">{selectedDesign.sku}</p>
                    </div>
                    <Button variant="ghost" className="h-10 gap-1.5 shrink-0" onClick={() => setSelectedDesign(null)}>
                      <X className="h-4 w-4" /> {t('filmPreprint.changeDesign')}
                    </Button>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label htmlFor="preprint-size">{t('filmPreprint.sizeLabel')}</Label>
                      <Input
                        id="preprint-size"
                        value={size}
                        onChange={(e) => setSize(e.target.value)}
                        placeholder={t('filmPreprint.sizePlaceholder')}
                        className="h-12 text-base"
                        autoComplete="off"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="preprint-qty">{t('filmPreprint.quantityLabel')}</Label>
                      <Input
                        id="preprint-qty"
                        type="number"
                        min={1}
                        max={500}
                        value={quantity}
                        onChange={(e) => setQuantity(Math.max(1, Math.min(500, Math.floor(Number(e.target.value) || 1))))}
                        className="h-12 text-base tabular-nums"
                      />
                    </div>
                  </div>

                  {/* Inline eligibility */}
                  {debouncedSize.length >= 1 && (
                    <div aria-live="polite">
                      {eligibilityQuery.isLoading || !eligibilityFresh ? (
                        <p className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Loader2 className="h-4 w-4 animate-spin" /> {t('filmPreprint.checkingEligibility')}
                        </p>
                      ) : eligibilityQuery.data?.eligible ? (
                        <p className="flex items-center gap-2 text-sm font-medium text-emerald-700 dark:text-emerald-300">
                          <CheckCircle className="h-4 w-4" /> {t('filmPreprint.eligible')}
                        </p>
                      ) : eligibilityQuery.data ? (
                        <div className="flex items-start gap-2 rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30 p-3">
                          <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0" />
                          <div>
                            <p className="text-sm font-semibold text-amber-800 dark:text-amber-200">{t('filmPreprint.ineligible')}</p>
                            <p className="text-sm text-amber-700 dark:text-amber-300">{eligibilityQuery.data.reason}</p>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  )}

                  <Button className="h-12 text-base gap-2 w-full sm:w-auto" onClick={handleAddLine} disabled={!canAddLine}>
                    <Plus className="h-5 w-5" /> {t('filmPreprint.addLine')}
                  </Button>
                </div>
              )}

              {/* Batch lines + create */}
              {lines.length > 0 && (
                <div className="space-y-2 pt-2">
                  <p className="text-sm font-medium text-muted-foreground">{t('filmPreprint.linesTitle')}</p>
                  <div className="rounded-lg border divide-y">
                    {lines.map((l, idx) => (
                      <div key={`${l.shopifySku}-${l.size}-${idx}`} className="flex items-center gap-3 px-4 py-3">
                        <div className="min-w-0 flex-1">
                          <p className="text-base font-medium truncate">{l.title ?? l.shopifySku}</p>
                          <p className="font-mono text-sm text-muted-foreground truncate">{l.shopifySku}</p>
                        </div>
                        <Badge variant="secondary" className="text-sm shrink-0 tabular-nums">
                          {t('filmPreprint.lineSummary', { quantity: l.quantity, size: l.size })}
                        </Badge>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-10 w-10 shrink-0"
                          onClick={() => handleRemoveLine(idx)}
                          aria-label={t('filmPreprint.removeLine')}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                  <Button
                    className="h-12 text-base gap-2 w-full"
                    onClick={handleCreateBatch}
                    disabled={createBatchMutation.isPending || lines.length === 0}
                  >
                    {createBatchMutation.isPending ? (
                      <><Loader2 className="h-5 w-5 animate-spin" /> {t('filmPreprint.creating')}</>
                    ) : (
                      <><Boxes className="h-5 w-5" /> {t('filmPreprint.createBatch', { count: totalFilms })}</>
                    )}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* (b) WORKLIST: the open batch, or every film waiting to print here. */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between gap-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Boxes className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                {batchId ? t('filmPreprint.worklistTitle') : t('filmPreprint.worklistAllTitle')}
              </CardTitle>
              {batchId && canCreate && (
                <Button variant="ghost" className="h-10 gap-1.5" onClick={() => setBatchId(null)}>
                  <ArrowLeft className="h-4 w-4" /> {t('filmPreprint.newBatch')}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="px-0 pb-2 pt-0">
            {filmsQuery.isLoading ? (
              <div className="py-8 text-center"><Loader2 className="h-6 w-6 mx-auto animate-spin text-muted-foreground" /></div>
            ) : (filmsQuery.data?.films ?? []).length === 0 ? (
              <p className="px-6 py-6 text-base text-muted-foreground text-center">
                {batchId ? t('filmPreprint.noFilms') : t('filmPreprint.worklistAllEmpty')}
              </p>
            ) : (
              <div className="divide-y">
                {(filmsQuery.data?.films ?? []).map((film) => (
                  <StockFilmRow
                    key={film.id}
                    film={film}
                    busy={filmBusy[film.id]}
                    needsManual={needsManualFilmIds.has(film.id)}
                    downloading={filmDownload.isDownloading(film.id)}
                    onDownload={() => void handleDownloadStockFilm(film)}
                    onPrint={() => handlePrintFilm(film)}
                    onRack={() => setRackTarget(film)}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* (c) SHELF STOCK — read-only. */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Archive className="h-5 w-5 text-sky-600 dark:text-sky-400" />
              {t('filmPreprint.shelfStockTitle')}
            </CardTitle>
          </CardHeader>
          <CardContent className="px-0 pb-2 pt-0">
            {shelfStockQuery.isLoading ? (
              <div className="py-8 text-center"><Loader2 className="h-6 w-6 mx-auto animate-spin text-muted-foreground" /></div>
            ) : (shelfStockQuery.data?.stock ?? []).length === 0 ? (
              <p className="px-6 py-6 text-base text-muted-foreground text-center">{t('filmPreprint.shelfStockEmpty')}</p>
            ) : (
              <div className="divide-y">
                {(shelfStockQuery.data?.stock ?? []).map((g) => {
                  const groupSize = sizeFromDesignKey(g.designKey);
                  return (
                    <div key={g.designKey} className="flex items-center gap-3 px-6 py-3">
                      <div className="min-w-0 flex-1">
                        <p className="font-mono text-base font-medium truncate">{g.shopifySku ?? g.designKey}</p>
                        {groupSize && <Badge variant="secondary" className="text-sm mt-1">{groupSize}</Badge>}
                      </div>
                      <Badge className="bg-sky-100 text-sky-800 hover:bg-sky-100 dark:bg-sky-900/50 dark:text-sky-200 text-base shrink-0 tabular-nums">
                        {t('filmPreprint.shelfStockCount', { count: g.availableCount })}
                      </Badge>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Shelf-slot picker (rack a stock film onto a shelf slot). */}
      <Dialog open={rackTarget !== null} onOpenChange={(open) => { if (!open) setRackTarget(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('filmPreprint.shelfPickerTitle')}</DialogTitle>
            <DialogDescription>{t('filmPreprint.shelfPickerDesc')}</DialogDescription>
          </DialogHeader>
          {storageQuery.isLoading ? (
            <div className="py-8 text-center"><Loader2 className="h-6 w-6 mx-auto animate-spin text-muted-foreground" /></div>
          ) : shelfSlots.length === 0 ? (
            <div className="flex items-start gap-2 rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30 p-4">
              <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0" />
              <p className="text-sm text-amber-800 dark:text-amber-200">{t('filmPreprint.noShelfSlots')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 max-h-[50vh] overflow-y-auto py-1">
              {shelfSlots.map((slot) => {
                const full = slot.capacity != null && slot.currentCount >= slot.capacity;
                const busy = rackTarget != null && filmBusy[rackTarget.id] === 'racking';
                return (
                  <button
                    key={slot.id}
                    disabled={full || busy || rackTarget == null}
                    onClick={() => rackTarget && handleRackToShelf(rackTarget, slot.id)}
                    className={cn(
                      'flex flex-col items-start gap-1 rounded-lg border-2 p-4 text-left transition-colors min-h-16',
                      full
                        ? 'opacity-50 cursor-not-allowed border-border'
                        : 'border-sky-300 dark:border-sky-700 hover:bg-sky-50 dark:hover:bg-sky-950/30',
                    )}
                  >
                    <span className="font-mono text-xl font-bold tabular-nums text-sky-700 dark:text-sky-300">{slot.label}</span>
                    <span className="text-sm text-muted-foreground tabular-nums">
                      {slot.capacity != null
                        ? t('filmPreprint.slotCountCapped', { count: slot.currentCount, capacity: slot.capacity })
                        : t('filmPreprint.slotCount', { count: slot.currentCount })}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ScanErrorModal {...feedback.modalProps} />
    </div>
  );
}

// ============================================================================
// StockFilmRow — one pre-printed stock film in the batch worklist
// ============================================================================

function StockFilmRow({
  film,
  busy,
  needsManual = false,
  downloading = false,
  onDownload,
  onPrint,
  onRack,
}: {
  film: StockFilm;
  busy?: 'printing' | 'racking';
  /** True when this film's drop did not happen: offers Download film. */
  needsManual?: boolean;
  downloading?: boolean;
  onDownload?: () => void;
  onPrint: () => void;
  onRack: () => void;
}) {
  const { t } = useTranslation('production');
  const size = sizeFromDesignKey(film.designKey);
  const isStored = film.status === 'STORED';

  return (
    <div className="flex flex-wrap items-center gap-3 px-6 py-4">
      <div className="min-w-0 flex-1">
        <p className="font-mono text-base font-medium truncate">{film.shopifySku ?? t('filmPreprint.unknownDesign')}</p>
        <div className="flex items-center gap-2 pt-1">
          {size && <Badge variant="secondary" className="text-sm">{size}</Badge>}
          <Badge className="bg-indigo-100 text-indigo-800 hover:bg-indigo-100 dark:bg-indigo-900/50 dark:text-indigo-200 text-sm">DTF</Badge>
        </div>
      </div>

      {isStored ? (
        <div className="flex items-center gap-1.5 text-emerald-700 dark:text-emerald-300 shrink-0">
          <CheckCircle className="h-5 w-5" />
          <span className="text-sm font-semibold">{t('filmPreprint.filmStatusStored')}</span>
        </div>
      ) : (
        <div className="flex items-center gap-2 shrink-0">
          {needsManual && onDownload && (
            <Button variant="outline" className="h-12 text-base gap-2" onClick={onDownload} disabled={!!busy || downloading}>
              {downloading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Download className="h-5 w-5" />}
              {t('filmPrint.downloadFilm')}
            </Button>
          )}
          <Button variant="outline" className="h-12 text-base gap-2" onClick={onPrint} disabled={!!busy}>
            {busy === 'printing' ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
            {t('filmPreprint.printFilm')}
          </Button>
          <Button
            className="h-12 text-base gap-2 bg-emerald-600 hover:bg-emerald-700 text-white"
            onClick={onRack}
            disabled={!!busy}
          >
            {busy === 'racking' ? <Loader2 className="h-5 w-5 animate-spin" /> : <Archive className="h-5 w-5" />}
            {t('filmPreprint.rackToShelf')}
          </Button>
        </div>
      )}
    </div>
  );
}
